/**
 * Form validation hook for authentication forms
 */

import { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { checkUsernameExists } from '../../../services/firebase/profileService';

/**
 * Hook for form validation in authentication forms
 * 
 * @param {Object} initialValues - Initial form values
 * @param {Function} validateFunction - Custom validation function
 * @returns {Object} Form validation state and handlers
 */
const useFormValidation = (initialValues, validateFunction) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear existing error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Special handling for username field
    if (name === 'username') {
      validateUsername(value);
    }
  };

  // Validate username with debounce
  const validateUsername = debounce(async (username) => {
    // Skip validation for empty or short usernames
    if (!username || username.length < 3) {
      setErrors(prev => ({
        ...prev,
        username: !username ? 'Username is required' : 
                 username.length < 3 ? 'Username must be at least 3 characters long' : undefined
      }));
      setCheckingUsername(false);
      return;
    }
    
    // Basic validation
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setErrors(prev => ({
        ...prev,
        username: 'Username can only contain letters, numbers, and underscores'
      }));
      setCheckingUsername(false);
      return;
    }
    
    if (username.length > 20) {
      setErrors(prev => ({
        ...prev,
        username: 'Username must be less than 20 characters'
      }));
      setCheckingUsername(false);
      return;
    }
    
    // Check if username exists in database
    setCheckingUsername(true);
    try {
      const exists = await checkUsernameExists(username);
      setErrors(prev => ({
        ...prev,
        username: exists ? 'Username already taken. Please choose a different one.' : undefined
      }));
    } catch (error) {
      console.error('Error in username check:', error);
      setErrors(prev => ({
        ...prev,
        username: 'Unable to check username availability. Please try again.'
      }));
    } finally {
      setCheckingUsername(false);
    }
  }, 500);

  // Validate form with custom validation function
  const validateForm = () => {
    const validationErrors = validateFunction(values);
    setErrors(prev => ({ ...prev, ...validationErrors }));
    return Object.keys(validationErrors).length === 0;
  };

  // Check if form is valid (no errors and required fields filled)
  useEffect(() => {
    const hasErrors = Object.values(errors).some(error => error);
    const hasRequiredFields = values.name && values.username && values.email && 
                             values.password && values.confirmPassword && values.university;
    setIsValid(!hasErrors && hasRequiredFields && !checkingUsername);
  }, [errors, values, checkingUsername]);

  // Handle form submission
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const formIsValid = validateForm();
    setIsSubmitting(formIsValid);
    return formIsValid;
  };

  // Reset form to initial values
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setIsSubmitting(false);
    setIsValid(false);
    setCheckingUsername(false);
  };

  // Set a specific error message
  const setError = (field, message) => {
    setErrors(prev => ({
      ...prev,
      [field]: message
    }));
  };

  // Clear a specific error
  const clearError = (field) => {
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };

  return {
    values,
    errors,
    isSubmitting,
    isValid,
    checkingUsername,
    handleChange,
    handleSubmit,
    resetForm,
    setError,
    clearError,
    setValues
  };
};

export default useFormValidation;